import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Tag } from "antd";
import { CloseOutlined, HighlightOutlined } from "@ant-design/icons";
import lang from "../helper/langHelper";

// Paints the analysis results (plagiarism / grammar / readability / AI /
// facts) directly inside the post editor, like the Writing Assistant, right
// after Analyze Content. Uses the browser's CSS Custom Highlight API, so the
// editor's text and DOM are never changed — the highlights are purely
// visual, follow the text as it is edited, and can be removed one by one.

const MODES = [
  { key: "plagiarism", label: "Plagiarism", color: "239,68,68" },
  { key: "grammar", label: "Grammar", color: "59,130,246" },
  { key: "readability", label: "Readability", color: "245,158,11" },
  { key: "ai", label: "AI Score", color: "168,85,247" },
  { key: "facts", label: "Fact Check", color: "20,184,166" },
];

const norm = (s = "") => String(s).replace(/\s+/g, "").toLowerCase();
const wordKey = (w = "") => String(w).toLowerCase().replace(/[^a-z]/g, "");

const getFlags = (mode, analysis) => {
  if (!analysis) return { words: {}, sentences: [] };
  if (mode === "readability") {
    const details = analysis.readability_details || {};
    const words = {};
    (details.word_issues || []).forEach((w) => {
      words[wordKey(w.word)] = { tip: w.reason };
    });
    const sentences = (details.sentence_issues || []).map((s) => ({ text: s.sentence, tip: s.tip }));
    return { words, sentences };
  }
  if (mode === "ai") {
    const sentences = (analysis.ai_detection?.blocks || [])
      .filter((b) => b.fake_pct >= 50)
      .map((b) => ({ text: b.text, tip: `Likely AI (${b.fake_pct}% confidence)` }));
    return { words: {}, sentences };
  }
  if (mode === "plagiarism") {
    const sentences = (analysis.plagiarism?.matches || [])
      .filter((m) => m.text)
      .map((m) => ({ text: m.text, tip: `${m.similarity}% match — ${m.url || "source unavailable"}` }));
    return { words: {}, sentences };
  }
  if (mode === "grammar") {
    const sentences = (analysis.grammar?.issues || [])
      .filter((iss) => iss.sentence)
      .map((iss) => ({ text: iss.sentence, tip: iss.message }));
    return { words: {}, sentences };
  }
  if (mode === "facts") {
    const sentences = (analysis.facts || [])
      .filter((f) => f.claim && f.verdict !== "Unverified")
      .map((f) => ({
        text: f.claim,
        tip: `${f.verdict}${f.confidence != null ? ` (${f.confidence}% confidence)` : ""}`,
      }));
    return { words: {}, sentences };
  }
  return { words: {}, sentences: [] };
};

const supportsHighlights = () =>
  typeof window !== "undefined" && typeof window.Highlight === "function" && window.CSS && window.CSS.highlights;

// Index of all the editor's text with whitespace removed, mapping each kept
// character back to its (text node, offset) — so a flagged sentence can be
// located even when it spans several text nodes (bold/italic runs, lines).
const buildIndex = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let squashed = "";
  const map = [];
  let node;
  while ((node = walker.nextNode())) {
    const v = node.nodeValue || "";
    for (let i = 0; i < v.length; i++) {
      if (!/\s/.test(v[i])) {
        squashed += v[i].toLowerCase();
        map.push([node, i]);
      }
    }
  }
  return { squashed, map };
};

const rangeForText = (index, text) => {
  const target = norm(text);
  if (!target) return null;
  const at = index.squashed.indexOf(target);
  if (at === -1) return null;
  const start = index.map[at];
  const end = index.map[at + target.length - 1];
  if (!start || !end) return null;
  const r = document.createRange();
  r.setStart(start[0], start[1]);
  r.setEnd(end[0], end[1] + 1);
  return r;
};

function PostAnalysisHighlights({ analysis }) {
  const rootRef = useRef(null);
  const entriesRef = useRef([]); // [{ mode, kind, key, range, tip }]
  const [enabled, setEnabled] = useState({}); // mode -> bool (default on)
  const [dismissed, setDismissed] = useState({}); // mode -> { key: true }
  const [popup, setPopup] = useState(null);
  const [counts, setCounts] = useState({});
  const supported = supportsHighlights();

  const available = useMemo(
    () =>
      MODES.filter((m) => {
        const { words, sentences } = getFlags(m.key, analysis);
        return Object.keys(words).length > 0 || sentences.length > 0;
      }),
    [analysis]
  );

  const getEditorRoot = () => {
    const scope = rootRef.current && rootRef.current.parentElement;
    return scope ? scope.querySelector(".public-DraftEditor-content") : null;
  };

  const clearAll = useCallback(() => {
    if (!supported) return;
    MODES.forEach((m) => window.CSS.highlights.delete(`post-hl-${m.key}`));
    entriesRef.current = [];
  }, [supported]);

  const paint = useCallback(() => {
    if (!supported) return;
    clearAll();
    const root = getEditorRoot();
    if (!root || !analysis) return;
    const index = buildIndex(root);
    const nextCounts = {};
    const entries = [];
    MODES.forEach((m) => {
      const { words, sentences } = getFlags(m.key, analysis);
      const gone = dismissed[m.key] || {};
      const ranges = [];
      let count = 0;
      sentences.forEach((s) => {
        const key = norm(s.text);
        if (gone[key]) return;
        const range = rangeForText(index, s.text);
        if (!range) return;
        count++;
        ranges.push(range);
        entries.push({ mode: m.key, kind: "sentence", key, range, tip: s.tip });
      });
      if (Object.keys(words).length) {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          const re = /[A-Za-z'’]+/g;
          let match;
          const v = node.nodeValue || "";
          while ((match = re.exec(v))) {
            const k = wordKey(match[0]);
            if (!words[k] || gone[`w:${k}`]) continue;
            const range = document.createRange();
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);
            count++;
            ranges.push(range);
            entries.push({ mode: m.key, kind: "word", key: `w:${k}`, range, tip: words[k].tip });
          }
        }
      }
      nextCounts[m.key] = count;
      if (enabled[m.key] !== false && ranges.length) {
        const hl = new window.Highlight(...ranges);
        hl.priority = m.key === "readability" ? 1 : 2;
        window.CSS.highlights.set(`post-hl-${m.key}`, hl);
      }
    });
    entriesRef.current = entries.filter((e) => enabled[e.mode] !== false);
    setCounts((prev) => (JSON.stringify(prev) === JSON.stringify(nextCounts) ? prev : nextCounts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis, dismissed, enabled, supported, clearAll]);

  // Repaint when the analysis/toggles/dismissals change, and (debounced)
  // whenever the editor re-renders its text — ranges die with their nodes.
  useEffect(() => {
    paint();
    const root = getEditorRoot();
    if (!root || !supported) return undefined;
    let timer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(paint, 150);
    });
    observer.observe(root, { subtree: true, childList: true, characterData: true });
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [paint, supported]);

  useEffect(() => () => clearAll(), [clearAll]);

  // Click on flagged text -> read the reason / remove that highlight. Listens
  // on the editor without preventing default, so normal editing is unaffected.
  useEffect(() => {
    const root = getEditorRoot();
    if (!root || !supported) return undefined;
    const onClick = (e) => {
      let node = null;
      let offset = 0;
      if (document.caretPositionFromPoint) {
        const p = document.caretPositionFromPoint(e.clientX, e.clientY);
        if (p) { node = p.offsetNode; offset = p.offset; }
      } else if (document.caretRangeFromPoint) {
        const r = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (r) { node = r.startContainer; offset = r.startOffset; }
      }
      if (!node) { setPopup(null); return; }
      const hits = entriesRef.current.filter((en) => {
        try { return en.range.isPointInRange(node, offset); } catch { return false; }
      });
      if (!hits.length) { setPopup(null); return; }
      hits.sort((a, b) => (a.kind === "word" ? 0 : 1) - (b.kind === "word" ? 0 : 1));
      const hit = hits[0];
      setPopup({
        x: Math.min(e.clientX, window.innerWidth - 300),
        y: e.clientY + 14,
        text: hit.range.toString(),
        mode: hit.mode,
        key: hit.key,
        tip: hit.tip,
      });
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [supported, analysis, available.length]);

  useEffect(() => {
    if (!popup) return undefined;
    const close = (e) => {
      if (e.target.closest && e.target.closest("[data-post-hl-popup]")) return;
      if (e.target.closest && e.target.closest(".public-DraftEditor-content")) return;
      setPopup(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [popup]);

  const removeOne = () => {
    if (!popup) return;
    setDismissed((prev) => ({ ...prev, [popup.mode]: { ...(prev[popup.mode] || {}), [popup.key]: true } }));
    setPopup(null);
  };

  const removeAll = () => {
    const all = {};
    MODES.forEach((m) => { all[m.key] = false; });
    setEnabled(all);
    setPopup(null);
  };

  if (!analysis || !available.length) return null;

  return (
    <div ref={rootRef} className="post-analysis-highlights" style={{ margin: "4px 0 16px" }}>
      <style>{`
        ${MODES.map(
          (m) => `::highlight(post-hl-${m.key}) { background-color: rgba(${m.color}, 0.32); }`
        ).join("\n")}
        ::highlight(post-hl-grammar) { text-decoration: underline wavy rgb(59,130,246); }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <HighlightOutlined style={{ color: "#888" }} />
        <span style={{ fontSize: 12, color: "#666" }}>{lang("Highlights")}:</span>
        {available.map((m) => {
          const on = enabled[m.key] !== false;
          return (
            <Tag.CheckableTag
              key={m.key}
              checked={on}
              onChange={(checked) => { setPopup(null); setEnabled((p) => ({ ...p, [m.key]: checked })); }}
              style={{
                border: `1px solid rgb(${m.color})`,
                background: on ? `rgba(${m.color}, 0.25)` : "transparent",
                color: "#333",
                fontSize: 12,
              }}
            >
              {lang(m.label)} ({counts[m.key] ?? 0})
            </Tag.CheckableTag>
          );
        })}
        <Button size="small" type="text" icon={<CloseOutlined />} onClick={removeAll}>
          {lang("Remove all highlights")}
        </Button>
      </div>
      {!supported && (
        <p style={{ fontSize: 12, color: "#b45309", margin: "6px 0 0" }}>
          {lang("Your browser can't show inline highlights — please use a recent Chrome or Edge.")}
        </p>
      )}
      <p style={{ fontSize: 11, color: "#999", margin: "4px 0 0" }}>
        {lang("Click any highlighted text in your post to see why it was flagged or remove that highlight.")}
      </p>

      {popup && (
        <div
          data-post-hl-popup
          style={{
            position: "fixed",
            top: popup.y,
            left: popup.x,
            zIndex: 2000,
            width: 280,
            background: "#fff",
            border: "1px solid #e8e8e8",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            padding: "10px 12px",
          }}
        >
          <CloseOutlined
            style={{ position: "absolute", top: 10, right: 10, cursor: "pointer", fontSize: 11, color: "#999" }}
            onClick={() => setPopup(null)}
          />
          <div style={{ fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 4 }}>
            {lang(MODES.find((m) => m.key === popup.mode).label)}
          </div>
          <p style={{ margin: "0 18px 6px 0", fontSize: 12, color: "#888", fontStyle: "italic", maxHeight: 40, overflow: "hidden" }}>
            "{popup.text.length > 80 ? `${popup.text.slice(0, 80)}…` : popup.text}"
          </p>
          {popup.tip && <p style={{ margin: "0 0 8px", fontSize: 13, color: "#333" }}>{popup.tip}</p>}
          <span
            role="button"
            tabIndex={0}
            onClick={removeOne}
            onKeyDown={(e) => { if (e.key === "Enter") removeOne(); }}
            style={{ fontSize: 12, color: "#ff4d4f", cursor: "pointer" }}
          >
            {lang("Remove this highlight")}
          </span>
        </div>
      )}
    </div>
  );
}

export default PostAnalysisHighlights;
